// Google Drive Picker integration.
//
// Loads Google Identity Services + the Drive Picker API on demand,
// requests an incremental drive.file scope, and opens the Picker so the
// user can attach files to an incident's Media tab without uploading.
//
// Why drive.file (not drive.readonly): drive.file is per-file, granted
// only for files the user picks via the Picker. It's non-sensitive so
// no Google security review is required. drive.readonly is sensitive
// and grants whole-Drive access — overkill for "attach a file".
//
// Why no backend tokens: each Picker open uses GIS TokenClient. First
// use prompts for consent; subsequent opens in the same browser session
// are silent (~1h token TTL with silent refresh while the user stays
// signed into Google). Storing refresh tokens would add encryption,
// rotation, and revocation overhead we don't need.

const GIS_SRC = "https://accounts.google.com/gsi/client";
const GAPI_SRC = "https://apis.google.com/js/api.js";

// Module-level Promises — idempotent under React StrictMode double-render.
let scriptsP = null;
let pickerLibP = null;
let tokenClient = null;

const loadOnce = (src) =>
  new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      // Already loaded; resolve next tick.
      resolve();
      return;
    }
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(s);
  });

const loadScripts = () => {
  if (!scriptsP) {
    scriptsP = Promise.all([loadOnce(GIS_SRC), loadOnce(GAPI_SRC)]);
  }
  return scriptsP;
};

const loadPickerLib = () => {
  if (!pickerLibP) {
    pickerLibP = new Promise((resolve, reject) => {
      if (!window.gapi) {
        reject(new Error("gapi not loaded"));
        return;
      }
      window.gapi.load("picker", {
        callback: () => resolve(),
        onerror: () => reject(new Error("Failed to load picker library")),
      });
    });
  }
  return pickerLibP;
};

const getEnv = () => {
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  const apiKey = process.env.REACT_APP_GOOGLE_DRIVE_API_KEY;
  const appId = process.env.REACT_APP_GOOGLE_DRIVE_APP_ID;
  return { clientId, apiKey, appId };
};

export function isDrivePickerConfigured() {
  const { clientId, apiKey, appId } = getEnv();
  return !!(clientId && apiKey && appId);
}

const getAccessToken = () => {
  const { clientId } = getEnv();
  if (!tokenClient) {
    tokenClient = window.google.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: "https://www.googleapis.com/auth/drive.file",
      callback: () => {},
    });
  }
  return new Promise((resolve, reject) => {
    tokenClient.callback = (resp) => {
      if (resp.error) reject(resp);
      else resolve(resp.access_token);
    };
    try {
      tokenClient.requestAccessToken({ prompt: "" });
    } catch (err) {
      reject(err);
    }
  });
};

/**
 * Open the Google Drive Picker. Calls onPick with an array of selected
 * doc objects (file_id, name, mimeType, thumbnailUrl, sizeBytes, etc)
 * and the OAuth access token used for the session.
 *
 * The token is handed back because attaching a file to an incident only needs
 * its metadata, but downloading the bytes (Watermarking) needs to call the
 * Drive API. drive.file covers exactly the files picked here, so the same
 * token authorizes that download without a broader scope.
 */
export async function openDrivePicker({ onPick, onCancel } = {}) {
  if (!isDrivePickerConfigured()) {
    throw new Error("Drive Picker is not configured (missing REACT_APP_GOOGLE_DRIVE_API_KEY or APP_ID)");
  }
  await loadScripts();
  await loadPickerLib();
  const { apiKey, appId } = getEnv();
  const token = await getAccessToken();

  // Multiple views = multiple tabs in the Picker. We expose:
  //   • My Drive (default tab) — the user's own files
  //   • Shared with me — files others shared with the user
  //   • Shared drives — files in any team Shared Drive
  // Each is a separate DocsView so they appear as separate tabs;
  // a single view with setEnableDrives(true) only shows the
  // Shared Drives tab and hides personal Drive.
  const myDriveView = new window.google.picker.DocsView(window.google.picker.ViewId.DOCS)
    .setIncludeFolders(true)
    .setSelectFolderEnabled(true)
    .setOwnedByMe(true);

  const sharedWithMeView = new window.google.picker.DocsView(window.google.picker.ViewId.DOCS)
    .setIncludeFolders(true)
    .setSelectFolderEnabled(true)
    .setOwnedByMe(false);

  const sharedDrivesView = new window.google.picker.DocsView(window.google.picker.ViewId.DOCS)
    .setIncludeFolders(true)
    .setSelectFolderEnabled(true)
    .setEnableDrives(true);

  const picker = new window.google.picker.PickerBuilder()
    .setOAuthToken(token)
    .setDeveloperKey(apiKey)
    // setAppId takes the GCP project NUMBER (12 digits), not the client ID.
    // Without this, drive.file scope grants don't propagate and the picker
    // silently no-ops on file selection.
    .setAppId(appId)
    .enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED)
    .enableFeature(window.google.picker.Feature.SUPPORT_DRIVES)
    .addView(myDriveView)
    .addView(sharedWithMeView)
    .addView(sharedDrivesView)
    .setCallback((data) => {
      const action = data[window.google.picker.Response.ACTION];
      if (action === window.google.picker.Action.PICKED) {
        const docs = data[window.google.picker.Response.DOCUMENTS] || [];
        if (typeof onPick === "function") onPick(docs, token);
      } else if (action === window.google.picker.Action.CANCEL) {
        if (typeof onCancel === "function") onCancel();
      }
    })
    .build();
  picker.setVisible(true);
}
